import { useMemo } from "react";
import type { IUser } from "@/core/auth";
import { useGetManagedPackThreads } from "@/payroll/pack-chat/manager-query";
import { PACK_CHAT_STATUS } from "@/payroll/pack-chat/types";
import {
  ST_ADJUSTMENTS_ACCESS,
  StAdjustmentsHeaderActions,
  StAdjustmentsPreviewHeaderActions,
} from "@/payroll/pay-breakdown/st-adjustments";
import {
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  PAY_BREAKDOWN_SECTION,
} from "@/payroll/pay-breakdown/types";
import { usePayBreakdownSection } from "@/payroll/pay-breakdown/use-pay-breakdown-section";
import { usePayrollSectionAccess } from "@/payroll/pay-breakdown/use-payroll-section-access";
import { getPayBreakdownVersionContext } from "@/payroll/pay-breakdown/utils";
import { usePayrollRosterVersion } from "@/payroll/roster/query";
import type { PayBreakdownPeriodState } from "./use-pay-breakdown-period";

type UsePayBreakdownSectionsParams = {
  businessId: string | null;
  user: IUser | null;
  selectedPeriodStart: string | undefined;
} & Pick<
  PayBreakdownPeriodState,
  | "business"
  | "output"
  | "isBusinessLoading"
  | "isPayPeriodsLoading"
  | "selectedPayPeriod"
  | "payPeriods"
>;

export const usePayBreakdownSections = ({
  businessId,
  user,
  selectedPeriodStart,
  business,
  output,
  isBusinessLoading,
  isPayPeriodsLoading,
  selectedPayPeriod,
  payPeriods,
}: UsePayBreakdownSectionsParams) => {
  const access = usePayrollSectionAccess({
    business,
    user,
    isAiScript: output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
  });
  const {
    hasAiScript,
    canViewRoster,
    canViewPayPlan,
    payrollEmailsAccess,
    canViewPackChat,
    stAdjustmentsAccess,
    showSectionTabs,
  } = access;
  const { data: managedThreads } = useGetManagedPackThreads(
    businessId,
    selectedPeriodStart ?? null,
    { enabled: canViewPackChat },
  );
  const stAdjustmentsSectionActions =
    stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview ? (
      <StAdjustmentsPreviewHeaderActions />
    ) : stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.enabled ? (
      <StAdjustmentsHeaderActions />
    ) : undefined;
  const isSectionRoutingReady =
    !!selectedPeriodStart &&
    !isBusinessLoading &&
    !isPayPeriodsLoading &&
    !access.isLoading;

  const { activeSection, setActiveSection } = usePayBreakdownSection({
    availability: access.availability,
    isReady: isSectionRoutingReady,
  });
  const openPackThreadCount = managedThreads?.open_count ?? 0;
  const isWorkspacePage =
    activeSection === PAY_BREAKDOWN_SECTION.breakdown
      ? output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript
      : true;

  const { rosterVersionNumber, currentRosterVersionNumber, rerunVersionInfo } =
    getPayBreakdownVersionContext(selectedPayPeriod, payPeriods);

  usePayrollRosterVersion(
    showSectionTabs && canViewRoster ? businessId : null,
    rosterVersionNumber ?? currentRosterVersionNumber,
  );

  const packChatOpenByTechnician = useMemo(
    () =>
      new Set(
        (managedThreads?.threads ?? [])
          .filter((thread) => thread.status === PACK_CHAT_STATUS.open)
          .map((thread) => thread.technician.id),
      ),
    [managedThreads?.threads],
  );

  return {
    hasAiScript,
    canViewRoster,
    payrollEmailsAccess,
    canViewPayPlan,
    canViewPackChat,
    stAdjustmentsAccess,
    stAdjustmentsSectionActions,
    showSectionTabs,
    activeSection,
    setActiveSection,
    openPackThreadCount,
    isWorkspacePage,
    rosterVersionNumber,
    currentRosterVersionNumber,
    rerunVersionInfo,
    packChatOpenByTechnician,
  };
};

export type PayBreakdownSectionsState = ReturnType<
  typeof usePayBreakdownSections
>;
